import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Loader2, ExternalLink, Search, AlertCircle } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';

const InvoiceApproversSection = ({
  formData,
  handleInputChange,
  availableApprovers = [],
  availableInternalApprovers = [],
  isLoading = false,
  isDark,
  embedded = false
}) => {
  const [showApproverDropdown, setShowApproverDropdown] = useState(false);
  const [approverSearch, setApproverSearch] = useState('');

  const approverDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (approverDropdownRef.current && !approverDropdownRef.current.contains(e.target)) {
        setShowApproverDropdown(false);
        setApproverSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const SMATA_INVITE_URLS = {
    'JSM_NB':       'https://cngproperty.my.smata.com/management/contacts',
    'strata_logic': 'https://stratalogic.my.smata.com/management/contacts',
    'JSM+':         'https://cdstrata.my.smata.com/management/contacts',
  };
  const smataInviteUrl = formData.databaseName
    ? (SMATA_INVITE_URLS[formData.databaseName] || 'https://jamesons.my.smata.com/management/contacts')
    : null;

  const selectedInternalApprovers = formData.selected_internal_approvers || [];
  const selectedExternalApprovers = formData.selected_external_approvers || [];

  // Filter both lists by search
  const filteredExternal = availableApprovers.filter(a =>
    a.name.toLowerCase().includes(approverSearch.toLowerCase())
  );
  const filteredInternal = availableInternalApprovers.filter(a =>
    a.name.toLowerCase().includes(approverSearch.toLowerCase())
  );

  const hasResults = filteredExternal.length > 0 || filteredInternal.length > 0;

  // External validation — only active when at least one external approver is selected
  const hasExternalApprovers = selectedExternalApprovers.length > 0;
  const externalNumRequired = Number(formData.num_approvers_required) || 0;
  const numApproversEmptyError =
    hasExternalApprovers && formData.num_approvers_required === ''
      ? 'You are required to fill this answer'
      : null;
  const numApproversZeroError =
    hasExternalApprovers && !numApproversEmptyError && externalNumRequired === 0
      ? 'Must be greater than 0'
      : null;
  const externalApproversNeededError =
    hasExternalApprovers &&
    !numApproversEmptyError &&
    !numApproversZeroError &&
    externalNumRequired > selectedExternalApprovers.length
      ? `Must be ≤ ${selectedExternalApprovers.length} (total selected external approvers)`
      : null;
  const numApproversError = numApproversEmptyError || numApproversZeroError || externalApproversNeededError;
  const minAmountError =
    hasExternalApprovers && (formData.min_approver_amount === '' || formData.min_approver_amount == null)
      ? 'You are required to fill this answer'
      : null;
  const noExternalSelectedError =
    externalNumRequired > 0 && selectedExternalApprovers.length === 0
      ? `Please select at least ${externalNumRequired} external approver${externalNumRequired > 1 ? 's' : ''}`
      : null;

  const handleApproverToggle = (approver, type) => {
    if (type === 'internal') {
      const isSelected = selectedInternalApprovers.some((a) => a.id === approver.id);
      const updated = isSelected
        ? selectedInternalApprovers.filter((a) => a.id !== approver.id)
        : [...selectedInternalApprovers, approver];
      handleInputChange('selected_internal_approvers', updated);
    } else {
      const isSelected = selectedExternalApprovers.some((a) => a.id === approver.id);
      const updated = isSelected
        ? selectedExternalApprovers.filter((a) => a.id !== approver.id)
        : [...selectedExternalApprovers, approver];
      handleInputChange('selected_external_approvers', updated);
    }
  };

  const handleRemoveApprover = (approverId, type) => {
    if (type === 'internal') {
      handleInputChange(
        'selected_internal_approvers',
        selectedInternalApprovers.filter((a) => a.id !== approverId)
      );
    } else {
      handleInputChange(
        'selected_external_approvers',
        selectedExternalApprovers.filter((a) => a.id !== approverId)
      );
    }
  };

  const totalSelected = selectedInternalApprovers.length + selectedExternalApprovers.length;

  const fieldLabel = `m-0 font-semibold text-[16px] leading-normal ${isDark ? 'text-white' : 'text-[#072837]'}`;
  const innerBg = isDark ? (embedded ? 'bg-[#000000]' : 'bg-[#161515]') : null;
  const fieldBox = `rounded-[8px] px-[12px] py-[12px] flex items-center w-full ${isDark ? `${innerBg} border-none` : 'bg-[#F5F5F5] border border-solid border-[#f8f9fa]'}`;
  const fieldInput = `flex-1 bg-transparent border-0 outline-none text-[16px] ${isDark ? 'text-white placeholder:text-[#b3b3b3] [&::-webkit-inner-spin-button]:[filter:invert(1)] [&::-webkit-outer-spin-button]:[filter:invert(1)]' : 'text-[#072837] placeholder:text-[#686868]'}`;
  const dropdownBox = `rounded-[8px] px-[12px] py-[12px] flex items-center justify-between cursor-pointer w-full ${isDark ? `${innerBg} border-none` : 'bg-[#F5F5F5] border border-solid border-[#f8f9fa]'}`;
  const dropdownContainer = `absolute w-full top-full left-0 mt-[2px] rounded-[4px] z-[1000] shadow-md overflow-hidden ${isDark ? `${innerBg} [border:1px_solid_#292929]` : 'bg-white [border:1px_solid_#ced4da]'}`;
  const dropdownList = `max-h-[200px] overflow-y-auto [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:rounded-full ${isDark ? `[&::-webkit-scrollbar-track]:${embedded ? 'bg-[#000000]' : 'bg-[#161515]'} [&::-webkit-scrollbar-thumb]:bg-[#292929] [&::-webkit-scrollbar-thumb]:hover:bg-[#4d4d4d]` : '[&::-webkit-scrollbar-track]:bg-[#f5f5f5] [&::-webkit-scrollbar-thumb]:bg-[#d9d9d9] [&::-webkit-scrollbar-thumb]:hover:bg-[#c0c0c0]'}`;
  const dropdownItemBase = `px-3 py-2 cursor-pointer text-[14px] font-medium`;
  const sectionDivider = `px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-[#b3b3b3] bg-[#111]' : 'text-[#686868] bg-[#f8f8f8]'}`;

  const renderSearchBox = () => (
    <div className={`px-[8px] py-[8px] ${isDark ? '[border-bottom:1px_solid_#292929]' : '[border-bottom:1px_solid_#e9e9e9]'}`}>
      <div className={`flex items-center gap-[6px] px-[8px] py-[6px] rounded-[4px] ${isDark ? 'bg-[#292929]' : 'bg-[#f5f5f5]'}`}>
        <Search className={`w-[12px] h-[12px] shrink-0 ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`} />
        <input
          type="text"
          className={`flex-1 bg-transparent border-0 outline-none text-[14px] ${isDark ? 'text-white placeholder:text-[#b3b3b3]' : 'text-[#072837] placeholder:text-[#686868]'}`}
          placeholder="Search..."
          value={approverSearch}
          onChange={(e) => setApproverSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
        {approverSearch && (
          <button
            type="button"
            className="p-0 border-0 bg-transparent cursor-pointer flex items-center"
            onClick={(e) => { e.stopPropagation(); setApproverSearch(''); }}
          >
            <X className={`w-[10px] h-[10px] ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`} />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`rounded-[10px] p-[20px] flex flex-col gap-[20px] shadow-[0px_0px_6px_1px_rgba(0,0,0,0.08)] w-full max-w-[822px] transition-colors duration-200 ${
        isDark ? 'bg-[#292929] border-none' : 'bg-[#fff5eb] border-none'
      }`}
    >
      {/* Header row: label + yes/no radio */}
      <div className="flex flex-col gap-[12px] w-full">
        <div className="flex items-center gap-[10px]">
          <p className={`m-0 font-semibold text-[16px] leading-normal ${isDark ? 'text-white' : 'text-[#072837]'}`}>
            Updates required to External or Building Manager Invoice Approvals?
          </p>
          {isLoading && (
            <Loader2 className={`w-[16px] h-[16px] animate-spin shrink-0 ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`} />
          )}
        </div>
        <RadioGroup
          className="flex flex-col gap-[12px]"
          value={formData.invoice_approvers_enabled ? 'yes' : 'no'}
          onValueChange={(v) => !isLoading && handleInputChange('invoice_approvers_enabled', v === 'yes')}
        >
          <label className={`inline-flex items-center gap-[8px] text-[14px] transition-colors duration-200 ${isDark ? 'text-white' : 'text-[#000]'}`}>
            <RadioGroupItem value="no" id="ia-no" />
            <span>No</span>
          </label>
          <label className={`inline-flex items-center gap-[8px] text-[14px] transition-colors duration-200 ${isDark ? 'text-white' : 'text-[#000]'}`}>
            <RadioGroupItem value="yes" id="ia-yes" />
            <span>Yes</span>
          </label>
        </RadioGroup>
      </div>

      {formData.invoice_approvers_enabled && (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center gap-[10px] py-[20px]">
              <Loader2 className={`w-[20px] h-[20px] animate-spin ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`} />
              <span className={`text-[14px] ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>
                Loading approver data...
              </span>
            </div>
          ) : (
            <>
              {/* ── Select Approvers (unified) ── */}
              <div className="flex flex-col gap-[16px] w-full">
                <label className={fieldLabel}>Select Approvers</label>

                <div className="relative w-full" ref={approverDropdownRef}>
                  <div
                    className={dropdownBox}
                    onClick={() => setShowApproverDropdown((v) => { if (v) setApproverSearch(''); return !v; })}
                  >
                    <span className={`text-[16px] ${totalSelected > 0 ? (isDark ? 'text-white' : 'text-[#072837]') : (isDark ? 'text-[#b3b3b3]' : 'text-[#686868]')}`}>
                      {totalSelected > 0 ? `${totalSelected} Selected` : 'Select approvers'}
                    </span>
                    <ChevronDown className={`w-[12px] h-[12px] transition-transform ${showApproverDropdown ? 'rotate-180' : ''} ${isDark ? 'text-white' : 'text-[#686868]'}`} />
                  </div>

                  {showApproverDropdown && (
                    <div className={dropdownContainer}>
                      {renderSearchBox()}
                      <div className={dropdownList}>
                        {!hasResults && (
                          <div className={`px-3 py-3 text-[14px] text-center ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>
                            No results found
                          </div>
                        )}

                        {/* External Approvers section */}
                        {filteredExternal.length > 0 && (
                          <>
                            <div className={sectionDivider}>External Approvers</div>
                            {filteredExternal.map((approver) => {
                              const isSelected = selectedExternalApprovers.some((a) => a.id === approver.id);
                              return (
                                <div
                                  key={`external-${approver.id}`}
                                  className={`${dropdownItemBase} ${
                                    isSelected
                                      ? isDark ? 'bg-[#cd6d2c] text-[#000000]' : 'bg-[#e9ecef] text-[#072837]'
                                      : isDark ? `${innerBg} hover:bg-[#292929] text-white` : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                                  }`}
                                  onClick={() => handleApproverToggle(approver, 'external')}
                                >
                                  {approver.name}
                                </div>
                              );
                            })}
                          </>
                        )}

                        {/* Building Manager Approvers section */}
                        {filteredInternal.length > 0 && (
                          <>
                            <div className={sectionDivider}>Building Manager Approvers</div>
                            {filteredInternal.map((approver) => {
                              const isSelected = selectedInternalApprovers.some((a) => a.id === approver.id);
                              return (
                                <div
                                  key={`internal-${approver.id}`}
                                  className={`${dropdownItemBase} ${
                                    isSelected
                                      ? isDark ? 'bg-[#cd6d2c] text-[#000000]' : 'bg-[#e9ecef] text-[#072837]'
                                      : isDark ? `${innerBg} hover:bg-[#292929] text-white` : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                                  }`}
                                  onClick={() => handleApproverToggle(approver, 'internal')}
                                >
                                  {approver.name}
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Selected chips */}
                {totalSelected > 0 && (
                  <div className="flex flex-wrap gap-[6px]">
                    {selectedInternalApprovers.map((approver) => (
                      <div
                        key={`chip-internal-${approver.id}`}
                        className={`bg-[#cd6d2c] flex items-center gap-[10px] px-[12px] py-[6px] rounded-[40px] ${isDark ? 'hover:bg-[#e37f3d]' : 'hover:bg-[#b35d24]'}`}
                      >
                        <span className="text-[16px] font-normal text-white">{approver.name}</span>
                        <button
                          type="button"
                          className="p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center"
                          style={{ width: '12px', height: '12px' }}
                          onClick={(e) => { e.stopPropagation(); handleRemoveApprover(approver.id, 'internal'); }}
                        >
                          <X className="w-[12px] h-[12px] text-white" />
                        </button>
                      </div>
                    ))}
                    {selectedExternalApprovers.map((approver) => (
                      <div
                        key={`chip-external-${approver.id}`}
                        className={`bg-[#cd6d2c] flex items-center gap-[10px] px-[12px] py-[6px] rounded-[40px] ${isDark ? 'hover:bg-[#e37f3d]' : 'hover:bg-[#b35d24]'}`}
                      >
                        <span className="text-[16px] font-normal text-white">{approver.name}</span>
                        <button
                          type="button"
                          className="p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center"
                          style={{ width: '12px', height: '12px' }}
                          onClick={(e) => { e.stopPropagation(); handleRemoveApprover(approver.id, 'external'); }}
                        >
                          <X className="w-[12px] h-[12px] text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {noExternalSelectedError && (
                  <div className="rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                    <AlertCircle className="w-[16px] h-[16px] flex-shrink-0" />
                    <span className="leading-[1.4]">{noExternalSelectedError}</span>
                  </div>
                )}
              </div>

              {/* ── Number of External Approvers Required ── */}
              <div className="flex flex-col gap-[16px] w-full">
                <label className={fieldLabel}>
                  Number of External Approvers Required
                  {hasExternalApprovers && <span className="text-red-500 ml-[3px]">*</span>}
                </label>
                <div className={`${fieldBox} ${numApproversError ? 'border border-solid border-red-500' : ''}`}>
                  <input
                    type="number"
                    min="1"
                    className={fieldInput}
                    placeholder="Enter number"
                    value={formData.num_approvers_required || ''}
                    onChange={(e) => handleInputChange('num_approvers_required', e.target.value)}
                  />
                </div>
                {numApproversError && (
                  <div className="rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                    <AlertCircle className="w-[16px] h-[16px] flex-shrink-0" />
                    <span className="leading-[1.4]">{numApproversError}</span>
                  </div>
                )}
              </div>

              {/* ── Minimum Amount ── */}
              <div className="flex flex-col gap-[16px] w-full">
                <label className={fieldLabel}>
                  Minimum Amount
                  {hasExternalApprovers && <span className="text-red-500 ml-[3px]">*</span>}
                </label>
                <div className={`${fieldBox} justify-between ${minAmountError ? 'border border-solid border-red-500' : ''}`}>
                  <input
                    type="number"
                    min="0"
                    className={fieldInput}
                    placeholder="Enter amount"
                    value={formData.min_approver_amount || ''}
                    onChange={(e) => handleInputChange('min_approver_amount', e.target.value)}
                  />
                  <span className={`text-[16px] shrink-0 ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>$</span>
                </div>
                {minAmountError && (
                  <div className="rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                    <AlertCircle className="w-[16px] h-[16px] flex-shrink-0" />
                    <span className="leading-[1.4]">{minAmountError}</span>
                  </div>
                )}
              </div>

              {/* ── Footer: Can't find the person? ── */}
              <div className={`rounded-[8px] p-[16px] flex items-center justify-between gap-[24px] ${isDark ? 'bg-[#000000]' : 'bg-[#F5F5F5]'}`}>
                <span className={`font-medium text-[16px] ${isDark ? 'text-white' : 'text-[#072837]'}`}>
                  Can't find the person?
                </span>
                <a
                  href={smataInviteUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!smataInviteUrl}
                  onClick={!smataInviteUrl ? (e) => e.preventDefault() : undefined}
                  className={`flex items-center gap-[4px] px-[16px] py-[12px] rounded-[12px] no-underline transition-colors ${
                    smataInviteUrl
                      ? 'bg-[#CD6D2C] hover:bg-[#b35d24] cursor-pointer'
                      : 'bg-[#d9d9d9] cursor-not-allowed'
                  }`}
                >
                  <span className="font-semibold text-[16px] text-white">Invite to SMATA</span>
                  <ExternalLink className="w-[14px] h-[14px] text-white" />
                </a>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default InvoiceApproversSection;
